import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  dashboardApi,
  LectureItem,
  RatingData,
  WatchDurationAxis,
  WatchDurationPoint,
  WatchDurationSeries,
  formatSecondsLabel,
} from '../../../services/admin/dashboardApi';
import { AdminCard } from '../common/AdminPrimitives';

interface DurationDataPoint {
  x: number;
  y: number;
  date: string;
  percentage: number;
}

const DashboardStatGraphs: React.FC = () => {
  const [ratingData, setRatingData] = useState<RatingData[]>([]);
  const [ratingLoading, setRatingLoading] = useState(true);
  const [ratingError, setRatingError] = useState<string | null>(null);
  const [durationData, setDurationData] = useState<WatchDurationPoint[]>([]);
  const [durationAxis, setDurationAxis] = useState<WatchDurationAxis>({ kind: 'percent' });
  const [durationLoading, setDurationLoading] = useState(true);
  const [durationError, setDurationError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [durationTooltip, setDurationTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    data: DurationDataPoint;
  } | null>(null);
  const [selectedFilter, setSelectedFilter] = useState('전체 강의');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [lectures, setLectures] = useState<LectureItem[]>([]);
  const [selectedLecture, setSelectedLecture] = useState<LectureItem | null>(null);

  const convertToGraphPoints = (data: WatchDurationPoint[]): DurationDataPoint[] => {
    const graphWidth = 609;
    const maxY = 160;

    if (data.length === 0) {
      return [];
    }

    return data.map((point) => {
      // position 은 서버가 duration(또는 진행률)로 정규화한 0~1 값이다.
      const x = data.length === 1 ? graphWidth / 2 : point.position * graphWidth;
      // retention 은 정의상 0~100 이므로 클램프하지 않는다.
      const y = maxY - (point.percentage / 100) * maxY;

      return {
        x: Math.round(x),
        y: Math.round(y),
        date: point.label,
        percentage: point.percentage,
      };
    });
  };

  const durationDataPoints = convertToGraphPoints(durationData);

  const generateSVGPath = (points: DurationDataPoint[], filled = false): string => {
    if (points.length === 0) {
      return '';
    }

    let path = `M ${points[0].x} ${points[0].y}`;

    for (let index = 1; index < points.length; index += 1) {
      const current = points[index];
      const previous = points[index - 1];
      const controlPointDistance = Math.abs(current.x - previous.x) * 0.5;
      const cp1x = previous.x + controlPointDistance;
      const cp2x = current.x - controlPointDistance;

      path += ` C ${cp1x} ${previous.y}, ${cp2x} ${current.y}, ${current.x} ${current.y}`;
    }

    if (filled) {
      const lastPoint = points[points.length - 1];
      path += ` L ${lastPoint.x} 160 L ${points[0].x} 160 Z`;
    }

    return path;
  };

  const fetchLectures = async () => {
    try {
      const lectureList = await dashboardApi.getLectureList();
      setLectures(Array.isArray(lectureList) ? lectureList : []);
    } catch {
      setLectures([]);
    }
  };

  const fetchCourseRatings = async () => {
    try {
      setRatingLoading(true);
      setRatingError(null);
      const data = await dashboardApi.getAverageRatingByCourse();
      setRatingData(data);
    } catch {
      setRatingError('강의 평점 데이터를 불러오는데 실패했습니다.');
    } finally {
      setRatingLoading(false);
    }
  };

  const formatUpdatedAt = (date: Date | null): string => {
    if (!date) {
      return '-';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}. ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const fetchWatchDurationData = useCallback(
    async (forceUpdate = false) => {
      try {
        setDurationLoading(true);
        setDurationError(null);

        let series: WatchDurationSeries;

        if (selectedFilter === '전체 강의') {
          series = await dashboardApi.getWatchDurationOverall();
        } else if (selectedLecture) {
          series = await dashboardApi.getWatchDurationByLecture(selectedLecture.id, forceUpdate);
        } else {
          series = { points: [], axis: { kind: 'percent' }, computedAt: null };
        }

        setDurationData(series.points);
        setDurationAxis(series.axis);

        const computed = series.computedAt ? new Date(series.computedAt) : null;
        setLastUpdated(computed && !Number.isNaN(computed.getTime()) ? computed : null);
      } catch {
        setDurationError('시청 지속 시간 데이터를 불러오는데 실패했습니다.');
      } finally {
        setDurationLoading(false);
      }
    },
    [selectedFilter, selectedLecture],
  );

  useEffect(() => {
    fetchLectures();
    fetchCourseRatings();
    fetchWatchDurationData();
  }, [fetchWatchDurationData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    if (!isDropdownOpen) {
      return undefined;
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <Grid>
      <Panel>
        <PanelHeader>
          <PanelTitle>강의 평점</PanelTitle>
          <PanelSubtitle>상위 5개 강의</PanelSubtitle>
        </PanelHeader>

        {ratingLoading ? (
          <CenterMessage>강의 평점 데이터를 불러오는 중...</CenterMessage>
        ) : ratingError ? (
          <CenterMessage>
            <div>{ratingError}</div>
            <RetryButton type="button" onClick={fetchCourseRatings}>
              다시 시도
            </RetryButton>
          </CenterMessage>
        ) : (
          <RatingList>
            {ratingData.map((course) => (
              <RatingRow key={course.courseId}>
                <CourseName title={course.courseTitle}>{course.courseTitle}</CourseName>
                <RatingValueWrap>
                  <StarIcon viewBox="0 0 17 16" fill="none" aria-hidden="true">
                    <path d="M9.241 0.938748C8.88009 0.755118 8.45313 0.755118 8.09221 0.938748C7.80257 1.08611 7.63627 1.35953 7.52537 1.5712C7.40828 1.79468 7.28385 2.09386 7.1385 2.44337L6.33496 4.3753C6.28676 4.4912 6.26068 4.55321 6.23885 4.59773C6.23227 4.61115 6.22773 4.61944 6.22512 4.62398C6.2211 4.62773 6.21664 4.63098 6.21183 4.63363C6.2067 4.63472 6.19741 4.63647 6.18262 4.63858C6.13353 4.64559 6.0665 4.65123 5.94138 4.66126L3.85573 4.82847C3.4784 4.8587 3.1554 4.88459 2.90667 4.92689C2.67109 4.96695 2.35967 5.04063 2.13001 5.27055C1.84384 5.55706 1.7119 5.96312 1.77502 6.36312C1.82567 6.68412 2.03431 6.92678 2.20134 7.09766C2.37771 7.27809 2.6238 7.48887 2.91129 7.73512L4.50034 9.09632C4.59567 9.17798 4.64659 9.22194 4.68218 9.25647C4.69291 9.26687 4.69939 9.27375 4.7029 9.27764C4.70523 9.28262 4.70694 9.28787 4.70798 9.29326C4.70743 9.29847 4.70623 9.30785 4.70366 9.32257C4.69516 9.37142 4.6798 9.43691 4.65068 9.55901L4.1652 11.5942C4.07736 11.9625 4.00216 12.2776 3.96553 12.5273C3.93084 12.7637 3.90467 13.0827 4.05237 13.3721C4.23643 13.7328 4.58184 13.9838 4.98176 14.0474C5.30271 14.0984 5.59796 13.975 5.81209 13.8689C6.03819 13.7569 6.31471 13.588 6.63775 13.3907L8.42336 12.3C8.53048 12.2346 8.58802 12.1998 8.63186 12.1766C8.64506 12.1696 8.65361 12.1655 8.65839 12.1634C8.66385 12.1627 8.66937 12.1627 8.67483 12.1634C8.67961 12.1655 8.68815 12.1696 8.70136 12.1766C8.74519 12.1998 8.80273 12.2346 8.90986 12.3L10.6955 13.3907C11.0185 13.588 11.295 13.7569 11.5211 13.8689C11.7353 13.9749 12.0305 14.0984 12.3515 14.0474C12.7514 13.9838 13.0968 13.7328 13.2808 13.3721C13.4285 13.0827 13.4024 12.7637 13.3677 12.5273C13.3311 12.2777 13.2559 11.9625 13.168 11.5943L12.6825 9.55901C12.6534 9.43691 12.6381 9.37142 12.6296 9.32257C12.627 9.30785 12.6258 9.29847 12.6252 9.29326C12.6263 9.28787 12.628 9.28262 12.6303 9.27764C12.6338 9.27375 12.6403 9.26687 12.651 9.25647C12.6866 9.22195 12.7375 9.17798 12.8329 9.09632L14.4219 7.73513C14.7094 7.48888 14.9555 7.27809 15.1319 7.09766C15.2989 6.92678 15.5075 6.68412 15.5582 6.36312C15.6213 5.96312 15.4894 5.55706 15.2032 5.27055C14.9735 5.04063 14.6621 4.96695 14.4265 4.92689C14.1778 4.88459 13.8548 4.8587 13.4775 4.82846L11.3918 4.66126C11.2667 4.65123 11.1997 4.64559 11.1506 4.63858C11.1358 4.63647 11.1265 4.63472 11.1214 4.63364C11.1166 4.63098 11.1121 4.62773 11.1081 4.62398C11.1055 4.61944 11.1009 4.61115 11.0944 4.59773C11.0725 4.55321 11.0465 4.4912 10.9982 4.3753L10.1947 2.4434C10.0494 2.09388 9.92494 1.79469 9.80785 1.5712C9.69695 1.35953 9.53064 1.08611 9.241 0.938748Z" fill="#296768" />
                  </StarIcon>
                  <RatingValue>{(course.averageRating ?? 0).toFixed(2)}</RatingValue>
                </RatingValueWrap>
              </RatingRow>
            ))}
            {Array.from({ length: Math.max(0, 5 - ratingData.length) }).map((_, index) => (
              <RatingRow key={`empty-${index}`}>
                <CourseName>-</CourseName>
                <RatingValueWrap style={{ opacity: 0.35 }}>
                  <StarIcon viewBox="0 0 17 16" fill="none" aria-hidden="true">
                    <path d="M9.241 0.938748C8.88009 0.755118 8.45313 0.755118 8.09221 0.938748C7.80257 1.08611 7.63627 1.35953 7.52537 1.5712C7.40828 1.79468 7.28385 2.09386 7.1385 2.44337L6.33496 4.3753C6.28676 4.4912 6.26068 4.55321 6.23885 4.59773C6.23227 4.61115 6.22773 4.61944 6.22512 4.62398C6.2211 4.62773 6.21664 4.63098 6.21183 4.63363C6.2067 4.63472 6.19741 4.63647 6.18262 4.63858C6.13353 4.64559 6.0665 4.65123 5.94138 4.66126L3.85573 4.82847C3.4784 4.8587 3.1554 4.88459 2.90667 4.92689C2.67109 4.96695 2.35967 5.04063 2.13001 5.27055C1.84384 5.55706 1.7119 5.96312 1.77502 6.36312C1.82567 6.68412 2.03431 6.92678 2.20134 7.09766C2.37771 7.27809 2.6238 7.48887 2.91129 7.73512L4.50034 9.09632C4.59567 9.17798 4.64659 9.22194 4.68218 9.25647C4.69291 9.26687 4.69939 9.27375 4.7029 9.27764C4.70523 9.28262 4.70694 9.28787 4.70798 9.29326C4.70743 9.29847 4.70623 9.30785 4.70366 9.32257C4.69516 9.37142 4.6798 9.43691 4.65068 9.55901L4.1652 11.5942C4.07736 11.9625 4.00216 12.2776 3.96553 12.5273C3.93084 12.7637 3.90467 13.0827 4.05237 13.3721C4.23643 13.7328 4.58184 13.9838 4.98176 14.0474C5.30271 14.0984 5.59796 13.975 5.81209 13.8689C6.03819 13.7569 6.31471 13.588 6.63775 13.3907L8.42336 12.3C8.53048 12.2346 8.58802 12.1998 8.63186 12.1766C8.64506 12.1696 8.65361 12.1655 8.65839 12.1634C8.66385 12.1627 8.66937 12.1627 8.67483 12.1634C8.67961 12.1655 8.68815 12.1696 8.70136 12.1766C8.74519 12.1998 8.80273 12.2346 8.90986 12.3L10.6955 13.3907C11.0185 13.588 11.295 13.7569 11.5211 13.8689C11.7353 13.9749 12.0305 14.0984 12.3515 14.0474C12.7514 13.9838 13.0968 13.7328 13.2808 13.3721C13.4285 13.0827 13.4024 12.7637 13.3677 12.5273C13.3311 12.2777 13.2559 11.9625 13.168 11.5943L12.6825 9.55901C12.6534 9.43691 12.6381 9.37142 12.6296 9.32257C12.627 9.30785 12.6258 9.29847 12.6252 9.29326C12.6263 9.28787 12.628 9.28262 12.6303 9.27764C12.6338 9.27375 12.6403 9.26687 12.651 9.25647C12.6866 9.22195 12.7375 9.17798 12.8329 9.09632L14.4219 7.73513C14.7094 7.48888 14.9555 7.27809 15.1319 7.09766C15.2989 6.92678 15.5075 6.68412 15.5582 6.36312C15.6213 5.96312 15.4894 5.55706 15.2032 5.27055C14.9735 5.04063 14.6621 4.96695 14.4265 4.92689C14.1778 4.88459 13.8548 4.8587 13.4775 4.82846L11.3918 4.66126C11.2667 4.65123 11.1997 4.64559 11.1506 4.63858C11.1358 4.63647 11.1265 4.63472 11.1214 4.63364C11.1166 4.63098 11.1121 4.62773 11.1081 4.62398C11.1055 4.61944 11.1009 4.61115 11.0944 4.59773C11.0725 4.55321 11.0465 4.4912 10.9982 4.3753L10.1947 2.4434C10.0494 2.09388 9.92494 1.79469 9.80785 1.5712C9.69695 1.35953 9.53064 1.08611 9.241 0.938748Z" fill="#296768" />
                  </StarIcon>
                  <RatingValue>0.00</RatingValue>
                </RatingValueWrap>
              </RatingRow>
            ))}
          </RatingList>
        )}
      </Panel>

      <Panel $wide>
        <PanelHeader>
          <HeaderLeft>
            <PanelTitle>강의 시청 지속 시간</PanelTitle>
            <RefreshMeta>
              <RefreshLabel>{formatUpdatedAt(lastUpdated)}</RefreshLabel>
              <RefreshButton type="button" onClick={() => fetchWatchDurationData(true)} aria-label="새로고침">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M13.5 2.5V5.5H10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </RefreshButton>
            </RefreshMeta>
          </HeaderLeft>
          <DropdownRoot data-dropdown>
            <DropdownButton type="button" onClick={() => setIsDropdownOpen((open) => !open)}>
              <DropdownText>{selectedLecture ? selectedLecture.title : selectedFilter}</DropdownText>
              <DropdownCaret as="svg" $open={isDropdownOpen} width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </DropdownCaret>
            </DropdownButton>
            {isDropdownOpen && (
              <DropdownMenu>
                <DropdownItem
                  $active={selectedFilter === '전체 강의'}
                  onClick={() => {
                    setSelectedFilter('전체 강의');
                    setSelectedLecture(null);
                    setIsDropdownOpen(false);
                  }}
                >
                  전체 강의
                </DropdownItem>
                {lectures.length > 0 ? (
                  lectures.map((lecture) => (
                    <DropdownItem
                      key={lecture.id}
                      $active={selectedLecture?.id === lecture.id}
                      onClick={() => {
                        setSelectedFilter('선택된 강의');
                        setSelectedLecture(lecture);
                        setIsDropdownOpen(false);
                      }}
                    >
                      {lecture.title}
                    </DropdownItem>
                  ))
                ) : (
                  <DropdownEmpty>강의를 불러오는 중...</DropdownEmpty>
                )}
              </DropdownMenu>
            )}
          </DropdownRoot>
        </PanelHeader>

        <GraphWrap>
          {[100, 75, 50, 25, 0].map((value) => (
            <AxisRow key={value}>
              <AxisLabel>{value}%</AxisLabel>
              <AxisDivider />
            </AxisRow>
          ))}

          <GraphOverlay>
            {durationLoading ? (
              <CenterMessage>시청 지속 시간 데이터를 불러오는 중...</CenterMessage>
            ) : durationError ? (
              <CenterMessage>
                <div>{durationError}</div>
                <RetryButton type="button" onClick={() => fetchWatchDurationData(true)}>
                  다시 시도
                </RetryButton>
              </CenterMessage>
            ) : durationDataPoints.length === 0 ? (
              <CenterMessage>선택한 항목의 시청 지속 시간 데이터가 없습니다.</CenterMessage>
            ) : (
              <ChartSvg viewBox="0 0 629 192">
                <defs>
                  <linearGradient id="retentionGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C5E0E0" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="#C5E0E0" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#C5E0E0" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <AreaPath d={generateSVGPath(durationDataPoints, true)} fill="url(#retentionGradient)" />
                <LinePath
                  d={generateSVGPath(durationDataPoints)}
                  fill="none"
                  stroke="#448181"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {durationDataPoints.map((point, index) => (
                  <g key={index}>
                    <DataPoint cx={point.x} cy={point.y} r="3" />
                    <circle
                      cx={point.x}
                      cy={point.y}
                      r="8"
                      fill="transparent"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(event) => {
                        const rect = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                        if (!rect) {
                          return;
                        }

                        setDurationTooltip({
                          visible: true,
                          x: rect.left + point.x * (rect.width / 629),
                          y: rect.top + point.y * (rect.height / 192),
                          data: point,
                        });
                      }}
                      onMouseLeave={() => setDurationTooltip(null)}
                    />
                  </g>
                ))}
                {durationData.length > 0 &&
                  [0, 0.25, 0.5, 0.75, 1].map((fraction) => {
                    const label =
                      durationAxis.kind === 'seconds'
                        ? formatSecondsLabel(fraction * durationAxis.duration)
                        : `${Math.round(fraction * 100)}%`;
                    return (
                      <AxisTimeLabel
                        key={fraction}
                        x={Math.round(fraction * 609)}
                        y={182}
                        textAnchor={fraction === 0 ? 'start' : fraction === 1 ? 'end' : 'middle'}
                      >
                        {label}
                      </AxisTimeLabel>
                    );
                  })}
              </ChartSvg>
            )}
          </GraphOverlay>
        </GraphWrap>
      </Panel>

      {durationTooltip?.visible && (
        <Tooltip style={{ left: durationTooltip.x, top: durationTooltip.y - 60 }}>
          <div>시청 지점: {durationTooltip.data.date}</div>
          <TooltipMuted>지속률: {(durationTooltip.data.percentage ?? 0).toFixed(2)}%</TooltipMuted>
        </Tooltip>
      )}
    </Grid>
  );
};

const drawLine = keyframes`
  from { stroke-dashoffset: 10000; }
  to { stroke-dashoffset: 0; }
`;

const fillArea = keyframes`
  from { opacity: 0; transform: scaleY(0); }
  to { opacity: 1; transform: scaleY(1); }
`;

const Grid = styled.div`
  position: relative;
  display: grid;
  width: 100%;
  grid-template-columns: minmax(320px, 1fr) minmax(680px, 725px);
  gap: 16px;

  @media (max-width: 1280px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled(AdminCard)<{ $wide?: boolean }>`
  display: flex;
  min-height: 304px;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 16px;
`;

const PanelTitle = styled.div`
  color: #171719;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
`;

const PanelSubtitle = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.43;
`;

const RefreshMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshLabel = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-weight: 500;
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: #448181;

  &:hover {
    background: rgba(68, 129, 129, 0.08);
  }
`;

const RatingList = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const CourseName = styled.div`
  flex: 1;
  overflow: hidden;
  color: #171719;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.43;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RatingValueWrap = styled.div`
  display: flex;
  width: 56px;
  align-items: center;
  gap: 4px;
  color: #296768;
`;

const StarIcon = styled.svg`
  width: 16px;
  height: 16px;
`;

const RatingValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
`;

const DropdownRoot = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 10px;
  background: #fff;
  padding: 8px 12px;
`;

const DropdownText = styled.span`
  max-width: 220px;
  overflow: hidden;
  color: #171719;
  font-size: 13px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownCaret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  color: #171719;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  display: flex;
  min-width: 220px;
  max-height: 220px;
  flex-direction: column;
  overflow-y: auto;
  border: 1px solid #eaebec;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const DropdownItem = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  padding: 10px 14px;
  overflow: hidden;
  color: ${({ $active }) => ($active ? '#448181' : '#171719')};
  background: ${({ $active }) => ($active ? 'rgba(68, 129, 129, 0.08)' : 'transparent')};
  font-size: 14px;
  line-height: 1.5;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background: ${({ $active }) => ($active ? 'rgba(68, 129, 129, 0.08)' : 'rgba(112, 115, 124, 0.04)')};
  }
`;

const DropdownEmpty = styled.div`
  padding: 10px 14px;
  color: #7d7d7d;
  font-size: 14px;
  text-align: center;
`;

const GraphWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 32px;
  color: #7d7d7d;
  font-size: 12px;
`;

const AxisRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AxisLabel = styled.div`
  width: 38px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-weight: 500;
`;

const AxisDivider = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const GraphOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 48px;
  right: 0;
  bottom: 0;
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 192px;
`;

const LinePath = styled.path`
  stroke-dasharray: 10000;
  stroke-dashoffset: 10000;
  animation: ${drawLine} 2.5s ease-out forwards;
`;

const AreaPath = styled.path`
  transform-origin: bottom;
  opacity: 0;
  animation: ${fillArea} 1.8s ease-in-out 1.2s forwards;
`;

const DataPoint = styled.circle`
  fill: #448181;
`;

const AxisTimeLabel = styled.text`
  fill: rgba(55, 56, 60, 0.61);
  font-size: 11px;
  font-weight: 500;
`;

const CenterMessage = styled.div`
  display: flex;
  min-height: 208px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #7d7d7d;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  background: #448181;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
`;

const Tooltip = styled.div`
  position: fixed;
  z-index: 9999;
  transform: translateX(-50%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px 12px;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
  white-space: nowrap;
`;

const TooltipMuted = styled.div`
  color: #c5e0e0;
  font-size: 11px;
`;

export default DashboardStatGraphs;
